import type { ReactNode } from 'react';
import { PolicyBadge } from '../../policy/policy_badge.js';
import { PersonAvatar } from '../../shared/person/person_row.js';
import './checkout_trip_summary.css';

/** Um serviço que a viagem JÁ tem (#3180), pronto para leitura. */
export interface CheckoutBookedService {
  id: string;
  title: string;
  /** Status do voucher. */
  status?: string;
  /** Valor já pago, formatado. */
  price?: string;
}

/** O viajante da linha de meta: quem dá o retrato (o primeiro) e o rótulo (o
 *  nome, ou "2 viajantes"). */
export interface CheckoutSummaryTraveler {
  name: string;
  photoUrl?: string | null;
  label: string;
}

export interface CheckoutTripSummaryProps {
  /** "Sua viagem". */
  kicker: string;
  /** Rótulo do selo "Dentro da política"; ausente ⇒ sem selo. */
  policyBadge?: string;
  /** "Viagem para São Paulo"; ausente ⇒ sem título. */
  title?: string;
  /** Datas · noites, já formatado; vazio quando o produto não as tem. */
  meta: string;
  traveler?: CheckoutSummaryTraveler;
  /** O que a viagem já tem, fora de qualquer conta. Some inteira quando vazia. */
  booked: { label: string; services: readonly CheckoutBookedService[] };
  /** Rótulo do carrinho ("Nesta compra"), só quando há `booked` para contrastar. */
  beingRequestedLabel: string;
  /** O detalhe de cada item (a linguagem é do produto). */
  children: ReactNode;
  /** O rodapé de preço (`CheckoutPriceSummary`). */
  footer: ReactNode;
}

/** O cartão "Sua viagem" do checkout: título, meta (datas · noites · viajantes), o que
 *  a viagem já tem, o detalhe do que está sendo comprado e, embaixo, o preço e o CTA
 *  (Figma 405-5518). */
export function CheckoutTripSummary({
  kicker,
  policyBadge,
  title,
  meta,
  traveler,
  booked,
  beingRequestedLabel,
  children,
  footer,
}: Readonly<CheckoutTripSummaryProps>) {
  const hasBooked = booked.services.length > 0;
  return (
    <div className="tsp">
      <div className="tsp-head">
        <span className="tsp-kicker">{kicker}</span>
        {/* Mesmo selo do cabeçalho do card de cotação e dos recibos. */}
        {policyBadge && <PolicyBadge tone="within" label={policyBadge} />}
      </div>
      {title && <h3 className="tsp-title">{title}</h3>}
      <p className="tsp-meta">
        {meta}
        {traveler && (
          <>
            {meta && ' · '}
            <span className="tsp-traveler">
              <PersonAvatar name={traveler.name} photoUrl={traveler.photoUrl} />
              {traveler.label}
            </span>
          </>
        )}
      </p>
      {hasBooked && <BookedServices label={booked.label} services={booked.services} />}
      {/* O rótulo do carrinho só existe quando há a outra seção para contrastar —
          sozinho ele seria ruído sobre o único bloco do cartão. */}
      {hasBooked && <SectionLabel>{beingRequestedLabel}</SectionLabel>}
      <div className="tsp-items">{children}</div>
      {footer}
    </div>
  );
}

/** O que a viagem JÁ tem: linhas só-leitura, em tom secundário, ACIMA do que está
 *  sendo comprado. Não entra em conta nenhuma — o total e o CTA seguem sendo só o
 *  carrinho. */
function BookedServices({
  label,
  services,
}: {
  readonly label: string;
  readonly services: readonly CheckoutBookedService[];
}) {
  return (
    <div className="grid gap-2">
      <SectionLabel>{label}</SectionLabel>
      <ul className="m-0 grid list-none gap-1.5 p-0">
        {services.map((service) => (
          <li
            className="grid gap-0.5 rounded-md border border-border bg-muted px-3 py-2"
            key={service.id}
          >
            <span className="text-caption font-medium text-foreground">{service.title}</span>
            <span className="text-caption inline-flex items-center gap-2 text-muted-foreground">
              {service.status && <span>{service.status}</span>}
              {service.price && <span>{service.price}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** O rótulo das duas seções ("Já na viagem" / "Nesta compra") — o contraste entre
 *  elas é o ponto, então a voz é a MESMA nos dois. */
function SectionLabel({ children }: { readonly children: string }) {
  return <span className="text-caption font-semibold text-muted-foreground">{children}</span>;
}
